import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ticketService, { 
  TICKET_STATUS_LABELS, 
  TICKET_STATUS_COLORS,
  TICKET_CATEGORY_LABELS,
  TICKET_STATUSES
} from '../services/ticketService';
import './AdminTicketDetail.css';

const AdminTicketDetail = () => {
  const { ticketId: ticketIdFromParams } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [ticket, setTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [resolution, setResolution] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState('');
  const repliesListRef = useRef(null);

  // 从路径中提取 ticketId（因为路由可能没有正确定义参数）
  const ticketId = useMemo(() => {
    // 首先尝试从 useParams 获取
    if (ticketIdFromParams) {
      return ticketIdFromParams;
    }
    // 如果 useParams 没有，从路径中提取
    const pathMatch = location.pathname.match(/\/sys-portal\/tickets\/(\d+)/);
    if (pathMatch && pathMatch[1]) {
      return pathMatch[1];
    }
    return null;
  }, [ticketIdFromParams, location.pathname]);

  useEffect(() => {
    const loadTicketDetail = async () => {
      // 验证 ticketId 是否存在且有效
      if (!ticketId || ticketId === 'undefined') {
        setError('工单ID无效');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // 确保 ticketId 是数字
        const ticketIdNum = parseInt(ticketId, 10);
        if (isNaN(ticketIdNum)) {
          setError('工单ID格式错误');
          setLoading(false);
          return;
        }
        
        const ticketData = await ticketService.getAdminTicket(ticketIdNum);
        setTicket(ticketData);
        setReplies(ticketData.replies || []);
        
        // 滚动到最新消息
        setTimeout(() => {
          if (repliesListRef.current) {
            repliesListRef.current.scrollTop = repliesListRef.current.scrollHeight;
          }
        }, 100);
        
      } catch (err) {
        console.error('Error loading ticket detail:', err);
        setError(err.message || '加载工单详情失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    };
    
    loadTicketDetail();
  }, [ticketId]);

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    
    if (!replyContent.trim()) {
      return;
    }
    
    // 确保 ticketId 是数字
    const ticketIdNum = parseInt(ticketId, 10);
    if (isNaN(ticketIdNum)) {
      setShowErrorMessage('工单ID格式错误');
      setTimeout(() => setShowErrorMessage(''), 3000);
      return;
    }
    
    setIsSubmittingReply(true);
    
    try {
      const replyData = await ticketService.addAdminReply(ticketIdNum, {
        content: replyContent.trim(),
        is_internal: false
      });
      
      setReplies(prev => [...prev, replyData]);
      setReplyContent('');
      
      // 重新加载工单详情以获取最新状态
      const ticketData = await ticketService.getAdminTicket(ticketIdNum);
      setTicket(ticketData);
      
      // 滚动到最新消息
      setTimeout(() => {
        if (repliesListRef.current) {
          repliesListRef.current.scrollTop = repliesListRef.current.scrollHeight;
        }
      }, 100);
      
      setShowSuccessMessage('回复成功！');
      setTimeout(() => setShowSuccessMessage(''), 3000);
      
    } catch (err) {
      console.error('Error submitting reply:', err);
      setShowErrorMessage(err.message || '提交回复失败，请稍后重试');
      setTimeout(() => setShowErrorMessage(''), 3000);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handleAssignTicket = async () => {
    if (!selectedAssignee) return;

    // 确保 ticketId 是数字
    const ticketIdNum = parseInt(ticketId, 10);
    if (isNaN(ticketIdNum)) {
      setShowErrorMessage('工单ID格式错误');
      setTimeout(() => setShowErrorMessage(''), 3000);
      return;
    }

    try {
      // 注意：这里需要将selectedAssignee转换为用户ID
      // 暂时使用assignee_id，如果selectedAssignee是字符串，需要先查找对应的用户ID
      await ticketService.assignTicket(ticketIdNum, parseInt(selectedAssignee) || null);
      
      // 重新加载工单详情
      const ticketData = await ticketService.getAdminTicket(ticketIdNum);
      setTicket(ticketData);
      
      setShowAssignModal(false);
      setSelectedAssignee('');
      
      setShowSuccessMessage('工单分配成功');
      setTimeout(() => setShowSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Assign ticket error:', error);
      setShowErrorMessage(error.message || '工单分配失败');
      setTimeout(() => setShowErrorMessage(''), 3000);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedStatus) return;

    // 状态转换验证
    const validTransitions = {
      'pending': 'in_progress',
      'in_progress': 'resolved'
    };

    if (ticket && validTransitions[ticket.status] !== selectedStatus) {
      setShowErrorMessage('状态转换不符合规则！工单状态必须按照顺序执行。');
      setTimeout(() => setShowErrorMessage(''), 3000);
      return;
    }

    try {
      const updateData = {
        status: selectedStatus
      };
      
      if (selectedStatus === 'resolved' && resolution.trim()) {
        updateData.resolution = resolution.trim();
      }
      
      // 确保 ticketId 是数字
      const ticketIdNum = parseInt(ticketId, 10);
      if (isNaN(ticketIdNum)) {
        setShowErrorMessage('工单ID格式错误');
        setTimeout(() => setShowErrorMessage(''), 3000);
        return;
      }
      
      await ticketService.updateTicketStatus(ticketIdNum, updateData);
      
      // 重新加载工单详情
      const ticketData = await ticketService.getAdminTicket(ticketIdNum);
      setTicket(ticketData);
      
      setShowStatusModal(false);
      setSelectedStatus('');
      setResolution('');
      
      setShowSuccessMessage('状态更新成功');
      setTimeout(() => setShowSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Update status error:', error);
      setShowErrorMessage(error.message || '状态更新失败');
      setTimeout(() => setShowErrorMessage(''), 3000);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  

  // 格式化用户名：将"客服管理员"改为"客服"
  const formatUserName = (nickname) => {
    if (nickname === '客服管理员') {
      return '客服';
    }
    return nickname;
  };

  // 当回复列表更新时，自动滚动到底部
  useEffect(() => {
    if (replies.length > 0 && repliesListRef.current) {
      setTimeout(() => {
        repliesListRef.current.scrollTop = repliesListRef.current.scrollHeight;
      }, 100);
    }
  }, [replies.length]);

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingText>加载中...</LoadingText>
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorMessage>{error}</ErrorMessage>
      </PageContainer>
    );
  }

  if (!ticket) {
    return (
      <PageContainer>
        <ErrorMessage>工单不存在</ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* 成功提示 */}
      {showSuccessMessage && (
        <ToastMessage type="success">{showSuccessMessage}</ToastMessage>
      )}
      
      {/* 错误提示 */}
      {showErrorMessage && (
        <ToastMessage type="error">{showErrorMessage}</ToastMessage>
      )}
      
        <TopSection>
          <BackButton onClick={() => navigate('/sys-portal/tickets')}>
            ← 返回
          </BackButton>
        </TopSection>
      
      <PageHeader>
        <PageTitle>工单详情 - {ticket.ticket_id}</PageTitle>
        <HeaderActions>
          <ActionButton onClick={() => setShowStatusModal(true)}>
            更新状态
          </ActionButton>
        </HeaderActions>
      </PageHeader>

      <ContentGrid>
        {/* 左侧：工单信息 */}
        <LeftColumn>
          {/* 工单基本信息 */}
          <TicketInfoCard>
            <TicketHeader>
              <TicketTitleRow>
                <TicketTitle>{ticket.title}</TicketTitle>
                <StatusBadge status={ticket.status}>
                  {TICKET_STATUS_LABELS[ticket.status]}
                </StatusBadge>
              </TicketTitleRow>
            </TicketHeader>
            
            <TicketMeta>
              <MetaItem>
                <MetaLabel>工单编号：</MetaLabel>
                #{ticket.ticket_id}
              </MetaItem>
              <MetaItem>
                <MetaLabel>分类：</MetaLabel>
                {TICKET_CATEGORY_LABELS[ticket.category]}
              </MetaItem>
              <MetaItem>
                <MetaLabel>创建时间：</MetaLabel>
                {formatDate(ticket.created_at)}
              </MetaItem>
              <MetaItem>
                <MetaLabel>更新时间：</MetaLabel>
                {formatDate(ticket.updated_at)}
              </MetaItem>
              <MetaItem>
                <MetaLabel>处理人：</MetaLabel>
                客服
              </MetaItem>
            </TicketMeta>
            
            <TicketDescription>
              <DescriptionLabel>问题描述：</DescriptionLabel>
              <DescriptionContent>{ticket.description}</DescriptionContent>
            </TicketDescription>
            
            {ticket.resolution && (
              <TicketResolution>
                <ResolutionLabel>解决方案：</ResolutionLabel>
                <ResolutionContent>{ticket.resolution}</ResolutionContent>
              </TicketResolution>
            )}
          </TicketInfoCard>

          {/* 用户信息 */}
          <UserInfoCard>
            <UserInfoTitle>用户信息</UserInfoTitle>
            <UserInfoContent>
              {/* 基本信息 */}
              <UserInfoSection>
                <UserInfoSectionTitle>基本信息</UserInfoSectionTitle>
                <UserInfoItem>
                  <UserInfoLabel>用户名：</UserInfoLabel>
                  {ticket.user_nickname}
                </UserInfoItem>
                <UserInfoItem>
                  <UserInfoLabel>用户ID：</UserInfoLabel>
                  {ticket.user_id_str || '-'}
                </UserInfoItem>
                <UserInfoItem>
                  <UserInfoLabel>邮箱：</UserInfoLabel>
                  {ticket.user_email || '未绑定'}
                </UserInfoItem>
                <UserInfoItem>
                  <UserInfoLabel>手机号：</UserInfoLabel>
                  {ticket.user_phone || '未绑定'}
                </UserInfoItem>
                <UserInfoItem>
                  <UserInfoLabel>注册时间：</UserInfoLabel>
                  {ticket.user_created_at ? formatDate(ticket.user_created_at).split(' ')[0] : '-'}
                </UserInfoItem>
              </UserInfoSection>

              {/* 账户信息 */}
              <UserInfoSection>
                <UserInfoSectionTitle>账户信息</UserInfoSectionTitle>
                <UserInfoItem>
                  <UserInfoLabel>用户等级：</UserInfoLabel>
                  <LevelBadge level={ticket.user_level}>{ticket.user_level || '-'}</LevelBadge>
                </UserInfoItem>
                <UserInfoItem>
                  <UserInfoLabel>积分：</UserInfoLabel>
                  <PointBadge>{ticket.user_point !== undefined ? ticket.user_point : '-'}</PointBadge>
                </UserInfoItem>
                <UserInfoItem>
                  <UserInfoLabel>工单数量：</UserInfoLabel>
                  {ticket.user_ticket_count !== undefined ? `${ticket.user_ticket_count}个` : '-'}
                </UserInfoItem>
              </UserInfoSection>

              {/* 职业信息 */}
              {(ticket.current_job || ticket.expect_job || ticket.expect_city) && (
                <UserInfoSection>
                  <UserInfoSectionTitle>职业信息</UserInfoSectionTitle>
                  {ticket.current_job && (
                    <UserInfoItem>
                      <UserInfoLabel>当前职位：</UserInfoLabel>
                      {ticket.current_job}
                    </UserInfoItem>
                  )}
                  {ticket.expect_job && (
                    <UserInfoItem>
                      <UserInfoLabel>期望职位：</UserInfoLabel>
                      {ticket.expect_job}
                    </UserInfoItem>
                  )}
                  {ticket.expect_city && (
                    <UserInfoItem>
                      <UserInfoLabel>期望城市：</UserInfoLabel>
                      {ticket.expect_city}
                    </UserInfoItem>
                  )}
                </UserInfoSection>
              )}

              {/* 最近工单 */}
              {ticket.recent_tickets && ticket.recent_tickets.length > 0 && (
                <UserInfoSection>
                  <UserInfoSectionTitle>最近工单 ({ticket.recent_tickets.length})</UserInfoSectionTitle>
                  <RecentTicketsList>
                    {ticket.recent_tickets.map((recentTicket) => (
                      <RecentTicketItem 
                        key={recentTicket.id}
                        onClick={() => navigate(`/sys-portal/tickets/${recentTicket.id}`)}
                      >
                        <RecentTicketTitle>{recentTicket.title}</RecentTicketTitle>
                        <RecentTicketMeta>
                          <StatusBadge status={recentTicket.status}>
                            {TICKET_STATUS_LABELS[recentTicket.status]}
                          </StatusBadge>
                          <RecentTicketTime>
                            {recentTicket.created_at ? formatDate(recentTicket.created_at).split(' ')[0] : '-'}
                          </RecentTicketTime>
                        </RecentTicketMeta>
                      </RecentTicketItem>
                    ))}
                  </RecentTicketsList>
                </UserInfoSection>
              )}
            </UserInfoContent>
          </UserInfoCard>
        </LeftColumn>

        {/* 右侧：回复记录和操作 */}
        <RightColumn>
          {/* 回复列表 */}
          <RepliesSection>
            <RepliesTitle>回复记录 ({replies.length})</RepliesTitle>
            
            <RepliesCard>
              {replies.length === 0 ? (
                <EmptyReplies>
                  <EmptyRepliesText>暂无回复</EmptyRepliesText>
                </EmptyReplies>
              ) : (
                <RepliesListContainer ref={repliesListRef}>
                  <RepliesList>
                    {replies.map(reply => {
                      // 判断是用户还是客服：如果回复的user_id等于工单的user_id，则是用户，否则是客服
                      const isUser = reply.user_id === ticket.user_id;
                      return (
                        <ReplyItem key={reply.id} isUser={isUser}>
                          <ReplyBubble isUser={isUser}>
                        <ReplyHeader>
                              <ReplyAuthor isUser={isUser}>{formatUserName(reply.user_nickname)}</ReplyAuthor>
                              <ReplyTime isUser={isUser}>{formatDate(reply.created_at)}</ReplyTime>
                        </ReplyHeader>
                            <ReplyContent isUser={isUser}>{reply.content}</ReplyContent>
                          </ReplyBubble>
                      </ReplyItem>
                      );
                    })}
                  </RepliesList>
                </RepliesListContainer>
              )}
            </RepliesCard>
          </RepliesSection>

          {/* 回复表单 */}
          <ReplyForm onSubmit={handleReplySubmit}>
            <ReplyFormTitle>添加回复</ReplyFormTitle>
            <ReplyTextArea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="请输入您的回复内容..."
              rows={4}
            />
            <ReplyFormActions>
              <SubmitReplyButton type="submit" disabled={isSubmittingReply || !replyContent.trim()}>
                {isSubmittingReply ? '提交中...' : '提交回复'}
              </SubmitReplyButton>
            </ReplyFormActions>
          </ReplyForm>
        </RightColumn>
      </ContentGrid>

      {/* 分配工单弹框 */}
      {showAssignModal && (
        <ModalOverlay onClick={() => setShowAssignModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>分配工单</ModalTitle>
              <CloseButton onClick={() => setShowAssignModal(false)}>×</CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <FormGroup>
                <Label>选择处理人</Label>
                <Select
                  value={selectedAssignee}
                  onChange={(e) => setSelectedAssignee(e.target.value)}
                >
                  <option value="">请选择处理人</option>
                  <option value="客服">客服</option>
                  <option value="客服小李">客服小李</option>
                  <option value="技术小王">技术小王</option>
                  <option value="产品经理">产品经理</option>
                </Select>
              </FormGroup>
            </ModalBody>
            
            <ModalFooter>
              <CancelButton onClick={() => setShowAssignModal(false)}>
                取消
              </CancelButton>
              <SubmitButton onClick={handleAssignTicket} disabled={!selectedAssignee}>
                确认分配
              </SubmitButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* 更新状态弹框 */}
      {showStatusModal && (
        <ModalOverlay onClick={() => setShowStatusModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>更新工单状态</ModalTitle>
              <CloseButton onClick={() => setShowStatusModal(false)}>×</CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <StatusUpdateNote>
                工单状态必须按照顺序执行：待处理 → 处理中 → 已解决
              </StatusUpdateNote>
              
              <FormGroup>
                <Label>新状态</Label>
                <Select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <option value="">请选择状态</option>
                  {/* 当前状态：pending -> 只能选择 in_progress */}
                  {ticket?.status === 'pending' && (
                    <option value={TICKET_STATUSES.IN_PROGRESS}>处理中</option>
                  )}
                  {/* 当前状态：in_progress -> 只能选择 resolved */}
                  {ticket?.status === 'in_progress' && (
                    <option value={TICKET_STATUSES.RESOLVED}>已解决</option>
                  )}
                  {/* 当前状态：resolved -> 不能更新状态 */}
                  {ticket?.status === 'resolved' && (
                    <option value="" disabled>已是最终状态，无法更新</option>
                  )}
                </Select>
              </FormGroup>
              
              {selectedStatus === 'resolved' && (
                <FormGroup>
                  <Label>解决方案 <RequiredMark>*</RequiredMark></Label>
                  <TextArea
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    placeholder="请输入解决方案..."
                    rows={4}
                    required
                  />
                </FormGroup>
              )}
            </ModalBody>
            
            <ModalFooter>
              <CancelButton onClick={() => setShowStatusModal(false)}>
                取消
              </CancelButton>
              <SubmitButton 
                onClick={handleUpdateStatus} 
                disabled={!selectedStatus || (selectedStatus === 'resolved' && !resolution.trim())}
              >
                确认更新
              </SubmitButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default AdminTicketDetail;

// 样式组件
const PageContainer = styled.div`
  padding: 8px 24px 24px 24px;
  background: #f8fafc;
  min-height: 100vh;
`;

const TopSection = styled.div`
  margin-bottom: 8px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const BackButton = styled.button`
  padding: 8px 16px;
  background: white;
  color: #667eea;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8fafc;
    border-color: #667eea;
  }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
  flex: 1;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TicketInfoCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
`;

const TicketHeader = styled.div`
  margin-bottom: 16px;
`;

const TicketTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

const TicketTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  line-height: 1.5;
  flex: 1;
`;

const StatusBadge = styled.span`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background: ${props => TICKET_STATUS_COLORS[props.status] || '#718096'};
  white-space: nowrap;
`;

const TicketMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

const MetaItem = styled.div`
  font-size: 14px;
  color: #718096;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetaLabel = styled.span`
  color: #a0aec0;
  font-weight: 500;
  min-width: 80px;
`;

const TicketDescription = styled.div`
  margin-bottom: 20px;
`;

const DescriptionLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const DescriptionContent = styled.div`
  font-size: 14px;
  color: #4a5568;
  line-height: 1.6;
  background: #f7fafc;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

const TicketResolution = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
`;

const ResolutionLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const ResolutionContent = styled.div`
  font-size: 14px;
  color: #4a5568;
  line-height: 1.6;
  background: #f0fff4;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #9ae6b4;
`;

const UserInfoCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
`;

const UserInfoTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 16px 0;
`;

const UserInfoContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserInfoItem = styled.div`
  font-size: 14px;
  color: #4a5568;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const UserInfoLabel = styled.span`
  color: #a0aec0;
  font-weight: 500;
  min-width: 80px;
`;

const UserInfoSection = styled.div`
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const UserInfoSectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 14px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid #e2e8f0;
`;

const LevelBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    const level = props.level || 'L0';
    if (level.startsWith('L0') || level.startsWith('L1')) return '#e2e8f0';
    if (level.startsWith('L2') || level.startsWith('L3')) return '#bee3f8';
    if (level.startsWith('L4') || level.startsWith('L5')) return '#c6f6d5';
    return '#fed7d7';
  }};
  color: ${props => {
    const level = props.level || 'L0';
    if (level.startsWith('L0') || level.startsWith('L1')) return '#4a5568';
    if (level.startsWith('L2') || level.startsWith('L3')) return '#2c5282';
    if (level.startsWith('L4') || level.startsWith('L5')) return '#2f855a';
    return '#c53030';
  }};
`;

const PointBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: white;
`;

const RecentTicketsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RecentTicketItem = styled.div`
  padding: 12px;
  background: #f7fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #edf2f7;
    border-color: #cbd5e0;
    transform: translateX(2px);
  }
`;

const RecentTicketTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #2d3748;
  margin-bottom: 8px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const RecentTicketMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const RecentTicketTime = styled.div`
  font-size: 11px;
  color: #718096;
`;

const RepliesSection = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
`;

const RepliesCard = styled.div`
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  min-height: 100px;
`;

const RepliesListContainer = styled.div`
  /* 超过3个记录时显示滚动条，每个记录大约120px高度，加上间距约16px，3个记录约360px */
  max-height: 360px;
  overflow-y: auto;
  overflow-x: hidden;
  
  /* 自定义滚动条样式 */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 3px;
    
    &:hover {
      background: #a0aec0;
    }
  }
  
  /* Firefox 滚动条样式 */
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #f1f1f1;
`;

const RepliesTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 20px 0;
`;

const EmptyReplies = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const EmptyRepliesText = styled.div`
  color: #718096;
  font-size: 14px;
`;

const RepliesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 0;
`;

const ReplyItem = styled.div`
  display: flex;
  justify-content: ${props => props.isUser ? 'flex-start' : 'flex-end'};
  align-items: flex-start;
  width: 100%;
`;

const ReplyBubble = styled.div`
  max-width: 70%;
  padding: 12px 16px;
  border-radius: ${props => props.isUser ? '12px 12px 12px 4px' : '12px 12px 4px 12px'};
  background: ${props => props.isUser ? '#e3f2fd' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: ${props => props.isUser ? '#1a202c' : 'white'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &::before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    ${props => props.isUser 
      ? `
        left: -8px;
        bottom: 12px;
        border-right: 8px solid #e3f2fd;
        border-top: 8px solid transparent;
        border-bottom: 8px solid transparent;
      `
      : `
        right: -8px;
        bottom: 12px;
        border-left: 8px solid #764ba2;
        border-top: 8px solid transparent;
        border-bottom: 8px solid transparent;
      `
    }
  }
  
  @media (max-width: 768px) {
    max-width: 85%;
  }
`;

const ReplyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  gap: 12px;
`;

const ReplyAuthor = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.isUser ? '#1976d2' : 'rgba(255, 255, 255, 0.95)'};
`;

const ReplyTime = styled.div`
  font-size: 11px;
  color: ${props => props.isUser ? '#64748b' : 'rgba(255, 255, 255, 0.8)'};
  white-space: nowrap;
`;

const ReplyContent = styled.div`
  font-size: 14px;
  color: ${props => props.isUser ? '#1a202c' : 'white'};
  line-height: 1.6;
  word-wrap: break-word;
  white-space: pre-wrap;
`;

const ReplyForm = styled.form`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
`;

const ReplyFormTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 16px 0;
`;

const ReplyTextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  color: #374151;
  background: white;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  transition: all 0.2s ease;
  margin-bottom: 16px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const ReplyFormActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SubmitReplyButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// 弹框样式
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0 24px;
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    color: #334155;
  }
`;

const ModalBody = styled.div`
  padding: 0 24px;
`;

const StatusUpdateNote = styled.div`
  background: #fef3c7;
  border: 1px solid #fbbf24;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #92400e;
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid #f1f5f9;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  color: #374151;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  color: #374151;
  background: white;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  background: white;
  color: #64748b;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    color: #374151;
  }
`;

const SubmitButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const LoadingText = styled.div`
  color: #718096;
  font-size: 14px;
`;

const ToastMessage = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  animation: slideIn 0.3s ease-out;
  display: flex;
  align-items: center;
  gap: 10px;
  
  ${props => props.type === 'success' && `
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
  `}
  
  ${props => props.type === 'error' && `
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
  `}
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  &::before {
    content: ${props => props.type === 'success' ? '"✓"' : '"✗"'};
    font-size: 18px;
    font-weight: bold;
  }
`;

const ErrorMessage = styled.div`
  background: #fff5f5;
  color: #c53030;
  padding: 16px 20px;
  border-radius: 8px;
  border: 1px solid #feb2b2;
  font-size: 14px;
  margin-bottom: 20px;
`;

const RequiredMark = styled.span`
  color: #ef4444;
  font-weight: 600;
`;
